/**
 * Image Compression Utility
 * 
 * Shared logic for compressing images before sending them to APIs (Vercel/Gemini).
 * Ensures payloads stay within limits (4.5MB for Vercel functions).
 */

export interface CompressionOptions {
    maxDimension?: number;
    quality?: number;
    threshold?: number; // Bytes, below which no compression happens
}

const DEFAULT_OPTIONS: CompressionOptions = {
    maxDimension: 1024,
    quality: 0.75,
    threshold: 500_000 // 500KB
};

/**
 * Compress a base64-encoded image to reduce payload size.
 * Resizes to max dimension and compresses to JPEG.
 */
export async function compressBase64Image(base64: string, options: CompressionOptions = {}): Promise<string> {
    const { maxDimension, quality, threshold } = { ...DEFAULT_OPTIONS, ...options };

    return new Promise((resolve, reject) => {
        // Skip if already small
        if (base64.length < (threshold || 500_000)) {
            // console.log('[Compression] Skipping small file');
            resolve(base64);
            return;
        }

        const img = new Image();
        // Handle both Data URL and raw base64
        if (base64.startsWith('data:')) {
            img.src = base64;
        } else {
            img.src = `data:image/jpeg;base64,${base64}`;
        }

        img.onload = () => {
            let width = img.width;
            let height = img.height;
            const max = maxDimension || 1024;

            // Resize logic
            if (width > height) {
                if (width > max) {
                    height *= max / width;
                    width = max;
                }
            } else {
                if (height > max) {
                    width *= max / height;
                    height = max;
                }
            }

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                resolve(base64); // Fallback to original
                return;
            }

            ctx.drawImage(img, 0, 0, width, height);

            // Compress to JPEG
            const dataUrl = canvas.toDataURL('image/jpeg', quality || 0.75);

            // Return raw base64 (strip prefix)
            const rawBase64 = dataUrl.split(',')[1];

            // Console log optimization stats
            const originalSize = (base64.length / 1024).toFixed(2);
            const newSize = (rawBase64.length / 1024).toFixed(2);
            console.log(`[Compression] ${originalSize}KB -> ${newSize}KB (Max: ${max}px, Q: ${quality})`);

            resolve(rawBase64);
        };
        img.onerror = (e) => {
            console.warn('[Compression] Failed to load image, using original', e);
            resolve(base64);
        };
    });
}
